import { isSafari } from 'react-device-detect';

import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { OnlinePayment, OnlinePaymentMethodLocalized } from 'generated/bffCrmfin/models';
import { PaymentGatewayPaymentRequest } from 'generated/bffPaymentGateway/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import dayjs from 'helpers/dayjs';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';

import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { PaymentGatewayPaymentPayload } from 'models/Billing';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { ContextOnlinePaymentData, setContextOnlinePaymentDataAction } from 'saga/context/onlinePayment/ducks';

/* STATE */
export interface OnlinePaymentState {
  onlinePaymentMethods: OnlinePaymentMethodLocalized[];
  onlinePayment: OnlinePayment | null;
}

/* ACTION TYPES */
export enum OnlinePaymentActionTypes {
  GetOnlinePaymentMethods = '@@OnlinePayment/GET_PAYMENT_METHODS',
  PostOnlinePayment = '@@OnlinePayment/POST_PAYMENT',
}

/* ACTIONS */
export const getOnlinePaymentMethodsActions = createApiActionCreators(OnlinePaymentActionTypes.GetOnlinePaymentMethods);
export const postOnlinePaymentActions = createApiActionCreators(OnlinePaymentActionTypes.PostOnlinePayment);

/* REDUCERS */
const initialState: OnlinePaymentState = {
  onlinePaymentMethods: [],
  onlinePayment: null,
};

const onlinePaymentMethods = createReducer(initialState.onlinePaymentMethods, {
  [OnlinePaymentActionTypes.GetOnlinePaymentMethods]: {
    [RequestActionTypes.SUCCESS]: (_state: OnlinePaymentMethodLocalized[], payload: OnlinePaymentMethodLocalized[]) => {
      return !isSafari
        ? payload
            .sort((a, b) => a.sequenceNumber! - b.sequenceNumber!)
            .filter((filter) => filter.specialMethodId !== 'APPLEPAY')
        : payload.sort((a, b) => a.sequenceNumber! - b.sequenceNumber!);
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const onlinePayment = createReducer(initialState.onlinePayment, {
  [OnlinePaymentActionTypes.PostOnlinePayment]: {
    [RequestActionTypes.SUCCESS]: (_state: OnlinePayment | null, payload: OnlinePayment) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

/* SELECTORS*/
const selectPayment = (state: AppState) => state.onlinePayment;

export const selectOnlinePaymentMethod = (state: AppState): OnlinePaymentMethodLocalized[] =>
  selectPayment(state).onlinePaymentMethods;

export default combineReducers<OnlinePaymentState>({
  onlinePaymentMethods,
  onlinePayment,
});

/* SAGA*/
function* cpGetOnlinePaymentMethods() {
  const request: ApiClientRequest = {
    apiCall: call(api.cpGetOnlinePaymentMethods, yield getApiUrl(environment.urlConfig.crmfin)),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getOnlinePaymentMethodsActions.success(resp.data));
  } else {
    yield put(getOnlinePaymentMethodsActions.failure());
  }
}

function* postPaymentGatewayPayment({ payload }: AppAction<PaymentGatewayPaymentPayload>) {
  const onlinePaymentRequest: PaymentGatewayPaymentRequest = {
    caRefNo: payload.caRefNo,
    cuRefNo: payload.cuRefNo,
    documentId: payload.documentId,
    installmentId: payload.installmentId,
    paymentMethodId: Number(payload.paymentMethodId),
    installmentDueDate: dayjs(payload.dueDate).utc().format('YYYY-MM-DD'),
    errorUrl: `${environment.home.slice(0, -1)}${buildRoute(ROUTES.OnlinePayment)}?documentId=${
      payload.documentId
    }&billingCaRefNo=${payload.caRefNo}&varSymbol=${payload.varSymbol}&dueDate=${dayjs(payload.dueDate)
      .utc()
      .format('YYYY-MM-DD')}&payAll=${true}&gatewayResult=error`,
    failUrl: `${environment.home.slice(0, -1)}${buildRoute(ROUTES.OnlinePayment)}?documentId=${
      payload.documentId
    }&billingCaRefNo=${payload.caRefNo}&varSymbol=${payload.varSymbol}&dueDate=${dayjs(payload.dueDate)
      .utc()
      .format('YYYY-MM-DD')}&payAll=${true}&gatewayResult=fail`,
    successUrl: `${environment.home.slice(0, -1)}${buildRoute(ROUTES.OnlinePaymentConfirmation)}`,
  };

  const request: ApiClientRequest = {
    apiCall: call(
      api.postPaymentGatewayPayment,
      yield getApiUrl(environment.urlConfig.paymentGateway),
      onlinePaymentRequest
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const onlinePaymentData: ContextOnlinePaymentData = {
      paymentId: resp.data.paymentId,
      amount: payload.amount,
    };

    yield put(setContextOnlinePaymentDataAction(onlinePaymentData));
    window.location.href = resp.data.url;
  } else {
    yield put(postOnlinePaymentActions.failure());
  }
}

export function* onlinePaymentSaga() {
  yield takeLatest(
    createActionType(OnlinePaymentActionTypes.GetOnlinePaymentMethods, RequestActionTypes.REQUEST),
    cpGetOnlinePaymentMethods
  );

  yield takeLatest(
    createActionType(OnlinePaymentActionTypes.PostOnlinePayment, RequestActionTypes.REQUEST),
    postPaymentGatewayPayment
  );
}
